'use client';

import { useCallback } from 'react';
import { PublicKey } from '@velocity-exchange/sdk';
import { getUserKey } from '@velocity-exchange/common';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import useDriftAccountsStore from 'src/stores/useDriftAccountsStore';
import { DEFAULT_PUBLIC_KEY } from 'src/@types/types';
import { notify } from 'src/utils/notifications';
import TransactionErrorHandler from 'src/utils/TransactionErrorHandler/TransactionErrorHandler';
import type { Adapter } from '@solana/wallet-adapter-base';
import invariant from 'tiny-invariant';
import { useGetTxParams } from 'src/hooks/driftActions/transactions/useGetTxParams';
import { UiEnhancedIWalletV2 } from 'src/utils/wallets/IWalletV2';
import { useSponsoredSignDriftActionWithFallback } from 'src/hooks/wallets/useSponsoredSignDriftActionWithFallback';

/**
 * Hook for updating the delegate authority of a subaccount.
 */
export const useUpdateSubaccountDelegate = () => {
	const driftClient = useDriftStore((s) => s.driftClient.client);
	const walletAdapter = useDriftStore((s) => s.wallet.current.adapter);
	const setDriftAccountsStore = useDriftAccountsStore((s) => s.set);
	const getTxParams = useGetTxParams();

	const updateDelegateAction = useCallback(
		async (
			wallet: UiEnhancedIWalletV2,
			accountId: number,
			delegate: string
		) => {
			invariant(driftClient, 'driftClient is required');

			const ix = await driftClient.getUpdateUserDelegateIx(
				new PublicKey(delegate),
				{ subAccountId: accountId }
			);

			await wallet.signAndSendTransaction(ix, getTxParams());
		},
		[driftClient, getTxParams]
	);

	const wrappedUpdateDelegate =
		useSponsoredSignDriftActionWithFallback(updateDelegateAction);

	const updateSubaccountDelegate = useCallback(
		async (accountId: number, delegate: string): Promise<boolean> => {
			if (!driftClient) return false;

			const toastId = `updatingSubaccountDelegate${accountId}`;

			notify({
				type: 'awaiting_wallet_confirmation',
				id: toastId,
				showUntilCancelled: true,
			});

			try {
				await wrappedUpdateDelegate(accountId, delegate);

				const isRemoval = delegate.toString() === DEFAULT_PUBLIC_KEY.toString();

				notify({
					id: toastId,
					type: 'success',
					message: `Subaccount delegate ${isRemoval ? 'removed' : 'updated'}.`,
					showUntilCancelled: false,
				});

				setDriftAccountsStore((s) => {
					const userKey = getUserKey(
						accountId,
						driftClient?.authority as PublicKey
					);
					if (s.accounts[userKey] !== undefined) {
						s.accounts[userKey].delegate = new PublicKey(delegate);
						s.accounts[userKey].isDelegatedAway = !isRemoval;
					}
				});

				return true;
			} catch (e: any) {
				TransactionErrorHandler.handleError({
					error: e,
					toastId,
					wallet: walletAdapter as Adapter,
				});
				return false;
			}
		},
		[driftClient, walletAdapter, setDriftAccountsStore, wrappedUpdateDelegate]
	);

	return { updateSubaccountDelegate };
};
